import classNames from 'classnames';

/**
 * Filters event badges from product badges
 * @param {Array} badges - Array of product badges
 * @returns {Object|null} - Event badge or null if not found
 */
export const getEventBadge = (badges) => {
  const itemBadges = badges?.filter((badge) => badge?.name === 'Event Badge');
  return itemBadges?.length ? itemBadges[0] : null;
};

/**
 * Gets event details by combining config and props
 * @param {Object} config - Configuration object from useConfig
 * @param {Object} props - Props containing eventLink, eventMessage, endDate, backgroundImage
 * @returns {Object} - Processed event details
 */
export const getEventDetails = (config, { eventLink, eventMessage, endDate, backgroundImage }) => {
  const defaultStartDate = '2024-11-30T03:00:00-04:00';
  const usedBackgroundImage = config.backgroundImage || backgroundImage;
  const usedEventMessage = config.eventMessage || eventMessage;
  const usedTimerEventMessage = config.eventMessageWithTimer || eventMessage;
  const usedEventLink = config.eventLink || eventLink;
  const usedEndDate = new Date(config.endDate || endDate).valueOf();
  const usedFallbackColor = config.fallbackColor;
  const usedTextColor = config.textColor || '';
  const eventStartDate = new Date(config.startDate || defaultStartDate).valueOf();
  const timerStartDate = config.timerStartDate ? new Date(config.timerStartDate).valueOf() : eventStartDate;
  const isTimerEnabled = config.showTimer || false;

  return {
    usedBackgroundImage,
    usedEventMessage,
    usedTimerEventMessage,
    usedEventLink,
    usedEndDate,
    usedFallbackColor,
    eventStartDate,
    timerStartDate,
    isTimerEnabled,
    usedTextColor
  };
};

/**
 * Gets style classNames for banner based on device type and text color
 * @param {boolean} isMobile - Whether the device is mobile
 * @param {string} textColor - Text color from config
 * @returns {Object} - Style object with classNames
 */
export const getStyle = (isMobile, textColor) => ({
  bannerWrapper: classNames(
    'sui-h-full',
    'sui-bg-cover',
    'sui-flex',
    'sui-items-center',
    'sui-justify-center',
    'hover:sui-no-underline',
    'sui-px-2',
    'sui-py-8',
    { 'sui-h3-display': !isMobile },
    { 'sui-h4-display': isMobile }
  ),
  bannerWithTimerWrapper: classNames(
    'sui-block',
    'sui-py-5',
    'sui-h-full',
    'sui-bg-cover',
    'hover:sui-no-underline'
  ),
  bannerText: classNames(
    'sui-uppercase',
    { 'sui-text-inverse': !textColor },
    { '@lg:sui-text-5xl @md:sui-text-4xl @sm:sui-text-2xl sui-text-base': !isMobile }
  )
});

export const useConfig = (useConfigService) => {
  const eventMessage = useConfigService('PIPEventMessage');
  const eventMessageWithTimer = useConfigService('PIPEventWithTimerMessage');
  const eventLink = useConfigService('PIPEventLink');
  const endDate = useConfigService('PIPEventEndDate');
  const backgroundImage = useConfigService('PIPEventBackgroundImage');
  const alwaysShow = useConfigService('PIPEventAlwaysShow');
  const startDate = useConfigService('PIPEventStartDate');
  const timerStartDate = useConfigService('PIPEventTimerStartDate');
  const showTimer = useConfigService('PIPActiveEventShowTimer');
  const fallbackColor = useConfigService('PIPEventFallbackColor');
  const textColor = useConfigService('PIPEventTextColor');

  return {
    eventMessage,
    eventMessageWithTimer,
    eventLink,
    endDate,
    backgroundImage,
    alwaysShow,
    startDate,
    timerStartDate,
    showTimer,
    fallbackColor,
    textColor
  };
};
